/**
 * Payables and settlement release — the screen where a pandit's money is let go.
 *
 * **The board's question is "what is stuck and why", not "what is held".** Every row carries the
 * server's `ReleaseDecision`, which reports *every* failing condition rather than the first one.
 * A screen that showed only a status would send an operator into each row to find out, and one
 * that re-derived eligibility from status and dates would agree with the release worker today
 * and disagree after the next edit — with a pandit paid early or not paid at all in between.
 *
 * **"Released" and "asked" are different outcomes.** Above the auto-release ceiling nothing
 * moves and a colleague has to look; the screen says which happened rather than showing one
 * success toast for both.
 *
 * **Payout runs are the second half, and they are real now.** A run is previewed before it is
 * built, and the batch is created from *that* snapshot rather than a fresh query — so the figures
 * somebody approves are the figures they read. The preview names every reason a provider is not
 * being paid, all of them at once.
 *
 * **Sending a batch to a bank still refuses.** No payout provider is wired, so `Execute` answers
 * 503 and the screen shows that refusal verbatim rather than dressing it as a queued transfer.
 * Recording an out-of-band NEFT with its UTR is the sanctioned path, and it is here.
 *
 * **Temple remittances are the third view.** Temple charges are pass-through money in `2002`; ops
 * transfer each temple's month by hand and record it with the UTR. The amount is the server's —
 * the record call carries none — and charges whose booking names no temple are shown, not paid.
 */
import { useState } from 'react';
import { StatusChip, type Tone } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, FilterBar, Money, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { newIdempotencyKey } from '../lib/api/idempotency';
import {
  useDecideApproval,
  useHoldPayable,
  usePayable,
  usePayables,
  useRequestRelease,
  useResumePayable,
  type PayableBoardRow,
} from '../lib/api/hooks/payables';
import {
  useCreatePayoutBatch,
  useExecutePayoutBatch,
  useMarkPayoutManual,
  usePayoutBatch,
  usePayoutBatches,
  usePreviewPayoutBatch,
  useRetryPayout,
  type PayoutPreviewGroup,
  type PayoutRow,
} from '../lib/api/hooks/payouts';
import {
  useRecordTempleRemittance,
  useTempleRemittanceMonth,
  type TempleOwedRow,
} from '../lib/api/hooks/templeRemittances';
import { AuditTrail, BankAccounts } from './FinanceTools';

const PAGE_SIZE = 50;
const ALL = 'every state';

/** The server's vocabulary (R18), not a friendlier one — ops read the same words in the logs. */
const STATES = [ALL, 'accrued', 'held', 'release_approved', 'in_payout', 'settled'];

const TABS = ['Breakdown', 'Holds', 'Approvals', 'Bank', 'History'];

/** The two halves of this screen: what is owed, and what is being sent. */
const VIEWS = [
  { key: 'payables', label: 'Payables' },
  { key: 'runs', label: 'Payout runs' },
  { key: 'temples', label: 'Temple remittances' },
] as const;

const BATCH_ALL = 'every run';
/** The server's vocabulary again — an operator reads these words in the logs too. */
const BATCH_STATES = [
  BATCH_ALL,
  'pending_approval',
  'approved',
  'completed',
  'partially_failed',
  'cancelled',
];

export function batchTone(status: string): Tone {
  if (status === 'completed') return 'success';
  if (status === 'partially_failed' || status === 'cancelled') return 'error';
  if (status === 'pending_approval') return 'warn';
  return 'neutral';
}

export function payoutTone(status: string): Tone {
  if (status === 'processed') return 'success';
  if (status === 'failed' || status === 'cancelled' || status === 'reversed') return 'error';
  if (status === 'created' || status === 'queued' || status === 'processing') return 'warn';
  return 'neutral';
}

/** Today, as the date input and the API both want it. */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function statusTone(status: string): Tone {
  if (status === 'settled') return 'success';
  if (status === 'on_hold_dispute' || status === 'written_off') return 'error';
  if (status === 'release_approved' || status === 'in_payout') return 'warn';
  return 'neutral';
}

/** `accrued` is expected, never owed — R15. The label says so on the board too. */
export function statusLabel(status: string): string {
  if (status === 'accrued') return 'expected';
  return status.replace(/_/g, ' ');
}

function PayableBoard({ view, onView }: ViewProps) {
  const { scope, toast } = useConsole();
  const [state, setState] = useState(ALL);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detailTab, setDetailTab] = useState('Breakdown');
  const [releasing, setReleasing] = useState(false);
  const [holding, setHolding] = useState(false);

  const board = usePayables({
    page,
    page_size: PAGE_SIZE,
    ...(state === ALL ? {} : { payable_status: state }),
  });
  const detail = usePayable(openId);
  const hold = useHoldPayable();
  const resume = useResumePayable();
  const release = useRequestRelease();
  const decide = useDecideApproval();

  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });

  if (openId !== null) {
    return (
      <DetailPage
        title={`Payable #${openId}`}
        tabs={TABS}
        activeTab={detailTab}
        onTab={setDetailTab}
        onClose={() => setOpenId(null)}
        actions={
          detail.isSuccess ? (
            <>
              <ScopeAction
                scope={scope}
                need="finance"
                size="sm"
                variant="secondary"
                disabled={!detail.data.decision.allowed && !detail.data.decision.needs_approval}
                onClick={() => setReleasing(true)}
              >
                {detail.data.decision.needs_approval ? 'Ask a colleague' : 'Release'}
              </ScopeAction>
              <ScopeAction
                scope={scope}
                need="finance"
                size="sm"
                variant="ghost"
                onClick={() => setHolding(true)}
              >
                Hold
              </ScopeAction>
            </>
          ) : null
        }
      >
        {detail.isPending && <LoadingState message="Reading the payable…" />}
        {detail.isError && (
          <ErrorState title="Could not read the payable" cause={errorMessage(detail.error)} />
        )}

        {detail.isSuccess && (
          <>
            {/* Never a bare "not yet". Every condition the server objected to, at once. */}
            {!detail.data.decision.allowed && (
              <Section
                title="Not releasable"
                note="Everything standing in the way, not just the first thing"
              >
                <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                  {detail.data.decision.blockers.map((b) => (
                    <li key={b} style={{ fontSize: 'var(--fs-admin-ui)' }}>
                      {b}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {detailTab === 'Breakdown' && (
              <Section title="What the pandit earns" note="The same figures their app shows">
                <KV
                  rows={[
                    ['Gross', <Money key="g" paise={detail.data.payable.gross_amount_paise} showPaise />],
                    ['Commission', <Money key="c" paise={-detail.data.payable.commission_paise} showPaise />],
                    ['TDS u/s 194-O', <Money key="t" paise={-detail.data.payable.tds_paise} showPaise />],
                    ['Adjustments', <Money key="a" paise={detail.data.payable.adjustment_paise} showPaise />],
                    ['Net payable', <Money key="n" paise={detail.data.payable.net_payable_paise} showPaise />],
                    ['Strategy', detail.data.payable.settlement_strategy.replace(/_/g, ' ')],
                    ['State', statusLabel(detail.data.payable.status)],
                  ]}
                />
              </Section>
            )}

            {detailTab === 'Holds' && (
              <Section title="Holds" note="Resolved ones stay — who stopped this, and how it ended">
                {detail.data.pauses.length === 0 ? (
                  <EmptyState
                    title="Nothing has ever held this payable"
                    body="A hold is a row rather than a flag, so two can be open at once and resolving one releases nothing on its own."
                  />
                ) : (
                  <DataTable
                    density="compact"
                    columns={[
                      { key: 'kind', label: 'Why' },
                      { key: 'ref', label: 'Reference' },
                      { key: 'ended', label: 'Ended' },
                      { key: 'act', label: '' },
                    ]}
                    rows={detail.data.pauses.map((p) => ({
                      id: p.id,
                      kind: p.kind.replace(/_/g, ' '),
                      ref: p.source_ref ?? '—',
                      // A resumed row without an action means it was resumed and nobody
                      // recorded how — a blank cell there would read as "still open", which is
                      // the opposite of what happened.
                      ended: p.resumed_at
                        ? (p.resume_action ?? 'resumed').replace(/_/g, ' ')
                        : 'open',
                      act: p.resumed_at ? null : (
                        <ScopeAction
                          scope={scope}
                          need="finance"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            resume.mutate(
                              { payableId: detail.data.payable.id, action: 'released' },
                              {
                                onSuccess: () =>
                                  toast({ tone: 'success', message: 'Resumed' }),
                                onError: fail,
                              },
                            )
                          }
                        >
                          Resume
                        </ScopeAction>
                      ),
                    }))}
                  />
                )}
              </Section>
            )}

            {detailTab === 'Approvals' && (
              <Section
                title="Release approvals"
                note="A second pair of eyes above the ceiling — never the person who asked"
              >
                {detail.data.approvals.length === 0 ? (
                  <EmptyState
                    title="Nobody has been asked"
                    body="Below the auto-release ceiling a release simply happens; above it, the ask is a row rather than a conversation."
                  />
                ) : (
                  <DataTable
                    density="compact"
                    columns={[
                      { key: 'reason', label: 'Asked because' },
                      { key: 'status', label: 'State' },
                      { key: 'act', label: '' },
                    ]}
                    rows={detail.data.approvals.map((a) => ({
                      id: a.id,
                      reason: a.request_reason,
                      status: a.status,
                      act:
                        a.status === 'pending' ? (
                          <div style={{ display: 'flex', gap: 'var(--sp-xs)' }}>
                            {(['approve', 'reject'] as const).map((d) => (
                              <ScopeAction
                                key={d}
                                scope={scope}
                                need="finance"
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  decide.mutate(
                                    { approvalId: a.id, decision: d },
                                    {
                                      onSuccess: () =>
                                        toast({ tone: 'success', message: `Request ${d}d` }),
                                      onError: fail,
                                    },
                                  )
                                }
                              >
                                {d === 'approve' ? 'Approve' : 'Reject'}
                              </ScopeAction>
                            ))}
                          </div>
                        ) : null,
                    }))}
                  />
                )}
              </Section>
            )}

            {detailTab === 'Bank' && (
              <BankAccounts panditProfileId={detail.data.payable.pandit_profile_id} />
            )}

            {detailTab === 'History' && (
              <AuditTrail entityType="provider_payable" entityId={detail.data.payable.id} />
            )}
          </>
        )}

        {releasing && detail.isSuccess && (
          <ConfirmAction
            open
            title={
              detail.data.decision.needs_approval
                ? 'Ask a colleague to approve this release?'
                : 'Release this payable?'
            }
            confirmLabel={detail.data.decision.needs_approval ? 'Ask' : 'Release'}
            phrase="RELEASE"
            consequences={
              detail.data.decision.needs_approval
                ? [
                    'This is above the auto-release ceiling, so nothing moves yet.',
                    'A second finance admin — not you — has to approve it.',
                  ]
                : [
                    'The pandit’s money is cleared for the next payout run.',
                    'A transfer is not an undoable screen action.',
                  ]
            }
            reason="Why — this is written down"
            onCancel={() => setReleasing(false)}
            onConfirm={(why) =>
              release.mutate(
                { payableId: detail.data.payable.id, reason: why },
                {
                  onSuccess: (result) => {
                    setReleasing(false);
                    toast({
                      tone: 'success',
                      message: result.released
                        ? 'Released — it joins the next payout run'
                        : 'Asked — a colleague has to approve it',
                    });
                  },
                  onError: (error) => {
                    setReleasing(false);
                    fail(error);
                  },
                },
              )
            }
          />
        )}

        {holding && detail.isSuccess && (
          <ConfirmAction
            open
            title="Hold this payable?"
            confirmLabel="Hold"
            phrase="HOLD"
            consequences={[
              'The money stops until somebody resumes it.',
              'Several holds can be open at once, and resolving one releases nothing on its own.',
            ]}
            reason="Why — the pandit is shown the category, never your words"
            onCancel={() => setHolding(false)}
            onConfirm={(why) =>
              hold.mutate(
                { payableId: detail.data.payable.id, kind: 'ops_hold', reason: why },
                {
                  onSuccess: () => {
                    setHolding(false);
                    toast({ tone: 'success', message: 'Held' });
                  },
                  onError: (error) => {
                    setHolding(false);
                    fail(error);
                  },
                },
              )
            }
          />
        )}
      </DetailPage>
    );
  }

  return (
    <>
      <FilterBar
        tabs={VIEWS}
        tab={view}
        onTab={onView}
        extra={
          <Select
            label="State"
            value={state}
            options={STATES}
            onChange={(next) => {
              setState(next);
              setPage(1);
            }}
          />
        }
      />

      {board.isPending && <LoadingState message="Reading the payables…" />}
      {board.isError && (
        <ErrorState title="Could not read the payables" cause={errorMessage(board.error)} />
      )}
      {board.isSuccess && board.data.items.length === 0 && (
        <EmptyState
          title="No payables in this state"
          body="A payable appears when a booking's money is captured and a pandit is assigned to it."
        />
      )}
      {board.isSuccess && board.data.items.length > 0 && (
        <DataTable
          density="compact"
          onRowClick={(row) => setOpenId(Number(row.id))}
          columns={[
            // Whose and for what: without these, a page of identical amounts cannot be told apart.
            { key: 'booking', label: 'For' },
            { key: 'pandit', label: 'To' },
            { key: 'net', label: 'Net payable', align: 'right', mono: true },
            { key: 'state', label: 'State' },
            { key: 'why', label: 'Why it is not moving' },
          ]}
          rows={board.data.items.map((row: PayableBoardRow) => ({
            id: row.payable.id,
            booking: `booking #${row.payable.booking_id}`,
            pandit: `pandit #${row.payable.pandit_profile_id}`,
            net: <Money paise={row.payable.net_payable_paise} showPaise />,
            state: (
              <StatusChip
                tone={statusTone(row.payable.status)}
                label={statusLabel(row.payable.status)}
              />
            ),
            // The board answers this without anybody opening a row.
            why: row.decision.allowed ? 'ready to release' : row.decision.blockers.join('; '),
          }))}
          footer={`${board.data.total} payable${board.data.total === 1 ? '' : 's'}`}
        />
      )}
    </>
  );
}

interface ViewProps {
  view: string;
  onView: (key: string) => void;
}

/**
 * Building, agreeing and settling a payout run.
 *
 * **The preview is asked for, never cached.** Its `snapshot_id` is what the create step is
 * checked against; a snapshot served from memory would let somebody build a batch from figures
 * that moved while the tab was open, which is the one thing the snapshot exists to prevent.
 */
function PayoutRuns({ view, onView }: ViewProps) {
  const { scope, toast } = useConsole();
  const [status, setStatus] = useState(BATCH_ALL);
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState<string | null>(null);
  const [executing, setExecuting] = useState<string | null>(null);
  const [settling, setSettling] = useState<{ payout: PayoutRow; key: string } | null>(null);
  const [retrying, setRetrying] = useState<{ payout: PayoutRow; key: string } | null>(null);
  const [utr, setUtr] = useState('');
  const [processedOn, setProcessedOn] = useState(todayIso());

  const batches = usePayoutBatches({
    page: 1,
    page_size: PAGE_SIZE,
    ...(status === BATCH_ALL ? {} : { batch_status: status }),
  });
  const detail = usePayoutBatch(openId);
  const preview = usePreviewPayoutBatch();
  const create = useCreatePayoutBatch();
  const execute = useExecutePayoutBatch();
  const markManual = useMarkPayoutManual();
  const retry = useRetryPayout();

  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });

  if (openId !== null) {
    return (
      <DetailPage title={`Payout run #${openId}`} onClose={() => setOpenId(null)}>
        {detail.isPending && <LoadingState message="Reading the run…" />}
        {detail.isError && (
          <ErrorState title="Could not read the run" cause={errorMessage(detail.error)} />
        )}

        {detail.isSuccess && (
          <>
            <Section
              title="The run"
              note="Approval and execution are two decisions, and two people"
              actions={
                detail.data.status === 'approved' ? (
                  <ScopeAction
                    scope={scope}
                    need="finance"
                    size="sm"
                    variant="secondary"
                    onClick={() => setExecuting(newIdempotencyKey())}
                  >
                    Execute
                  </ScopeAction>
                ) : null
              }
            >
              <KV
                rows={[
                  ['Run date', detail.data.batch_on],
                  [
                    'State',
                    <StatusChip
                      key="s"
                      tone={batchTone(detail.data.status)}
                      label={detail.data.status.replace(/_/g, ' ')}
                    />,
                  ],
                  ['Payouts', String(detail.data.payout_count)],
                  ['Total', <Money key="t" paise={detail.data.total_amount_paise} showPaise />],
                  ['Built by', `admin #${detail.data.created_by_admin_id}`],
                  [
                    'Agreed by',
                    detail.data.approved_by_admin_id === null
                      ? 'nobody yet'
                      : `admin #${detail.data.approved_by_admin_id}`,
                  ],
                ]}
              />
            </Section>

            <Section
              title="Payouts"
              note="One per provider. Every line is frozen at the amount it was built with."
            >
              <DataTable
                density="compact"
                columns={[
                  { key: 'code', label: 'Payout' },
                  { key: 'dest', label: 'To' },
                  { key: 'amount', label: 'Amount', align: 'right', mono: true },
                  { key: 'state', label: 'State' },
                  { key: 'utr', label: 'UTR' },
                  { key: 'act', label: '' },
                ]}
                rows={detail.data.payouts.map((p) => ({
                  id: p.id,
                  code: p.public_code,
                  dest: `••••${p.bank_account_last4}`,
                  amount: <Money paise={p.amount_paise} showPaise />,
                  state: <StatusChip tone={payoutTone(p.status)} label={p.status} />,
                  // A failure without its reason sends somebody to the logs for the one fact
                  // that decides what to do next.
                  utr: p.utr ?? p.failure_reason ?? '—',
                  act:
                    p.status === 'failed' ? (
                      <ScopeAction
                        scope={scope}
                        need="finance"
                        size="sm"
                        variant="ghost"
                        onClick={() => setRetrying({ payout: p, key: newIdempotencyKey() })}
                      >
                        Retry
                      </ScopeAction>
                    ) : p.status === 'created' ? (
                      <ScopeAction
                        scope={scope}
                        need="finance"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setUtr('');
                          setProcessedOn(todayIso());
                          setSettling({ payout: p, key: newIdempotencyKey() });
                        }}
                      >
                        Record NEFT
                      </ScopeAction>
                    ) : null,
                }))}
              />
            </Section>
          </>
        )}

        {executing !== null && (
          <ConfirmAction
            open
            title="Send this run to the payout provider?"
            confirmLabel="Execute"
            phrase="EXECUTE"
            tone="error"
            consequences={[
              'Every payout in this run is handed to the bank.',
              'While payouts are set to go by hand, this refuses rather than pretend — record the NEFT with its UTR instead.',
            ]}
            reason="Why — this is written down either way"
            onCancel={() => setExecuting(null)}
            onConfirm={(why) =>
              execute.mutate(
                { batchId: openId, reason: why, key: executing },
                {
                  onSuccess: (run) => {
                    setExecuting(null);
                    toast({
                      tone: 'success',
                      message: `Sent to the provider — the run is ${run.status.replace(/_/g, ' ')}`,
                    });
                  },
                  onError: (error) => {
                    setExecuting(null);
                    fail(error);
                  },
                },
              )
            }
          />
        )}

        {settling !== null && (
          <ConfirmAction
            open
            title={`Record an NEFT for ${settling.payout.public_code}?`}
            confirmLabel="Record"
            phrase="RECORD"
            consequences={[
              'This says the money has already left our account.',
              'The payables behind it are settled and will not appear in another run.',
            ]}
            reason="Why — how this was sent"
            onCancel={() => setSettling(null)}
            onConfirm={(why) =>
              markManual.mutate(
                {
                  payoutId: settling.payout.id,
                  utr,
                  processedOn,
                  reason: why,
                  key: settling.key,
                },
                {
                  onSuccess: () => {
                    setSettling(null);
                    toast({ tone: 'success', message: 'Recorded' });
                  },
                  onError: fail,
                },
              )
            }
          >
            <Input
              label="UTR"
              value={utr}
              onChange={setUtr}
              placeholder="HDFCN23456789"
              helper="The bank's reference — the only thing reconciliation can match against the statement"
            />
            <Input label="Sent on" value={processedOn} onChange={setProcessedOn} />
          </ConfirmAction>
        )}

        {retrying !== null && (
          <ConfirmAction
            open
            title={`Put ${retrying.payout.public_code} back in play?`}
            confirmLabel="Retry"
            phrase="RETRY"
            consequences={[
              'This does not send anything. It re-claims the payables the failure returned to the queue.',
              'It refuses if any of them has already been picked up by a newer run.',
            ]}
            reason="Why — what was fixed"
            onCancel={() => setRetrying(null)}
            onConfirm={(why) =>
              retry.mutate(
                { payoutId: retrying.payout.id, reason: why, key: retrying.key },
                {
                  onSuccess: () => {
                    setRetrying(null);
                    toast({ tone: 'success', message: 'Back in play' });
                  },
                  onError: (error) => {
                    setRetrying(null);
                    fail(error);
                  },
                },
              )
            }
          />
        )}
      </DetailPage>
    );
  }

  return (
    <>
      <FilterBar
        tabs={VIEWS}
        tab={view}
        onTab={onView}
        extra={
          <Select label="Run state" value={status} options={BATCH_STATES} onChange={setStatus} />
        }
        right={
          <ScopeAction
            scope={scope}
            need="finance"
            size="sm"
            variant="secondary"
            onClick={() =>
              preview.mutate(undefined, { onSuccess: () => undefined, onError: fail })
            }
          >
            Preview today’s run
          </ScopeAction>
        }
      />

      {preview.isSuccess && (
        <Section
          title="What today’s run would pay"
          note="Nothing is created yet — this is the assembly the run would be built from"
          actions={
            <ScopeAction
              scope={scope}
              need="finance"
              size="sm"
              variant="secondary"
              disabled={preview.data.eligible_total_paise === 0}
              onClick={() => setCreating(newIdempotencyKey())}
            >
              Build this run
            </ScopeAction>
          }
        >
          <KV
            rows={[
              ['Owed in total', <Money key="o" paise={preview.data.total_paise} showPaise />],
              [
                'Would actually move',
                <Money key="e" paise={preview.data.eligible_total_paise} showPaise />,
              ],
            ]}
          />
          {preview.data.groups.length === 0 ? (
            <EmptyState
              title="Nobody is waiting to be paid"
              body="A provider appears here once their payable has been released and has sat out the cutoff."
            />
          ) : (
            <DataTable
              density="compact"
              columns={[
                { key: 'pandit', label: 'Provider' },
                { key: 'dest', label: 'To' },
                { key: 'count', label: 'Payables', align: 'right' },
                { key: 'total', label: 'Total', align: 'right', mono: true },
                { key: 'why', label: 'Why it is not moving' },
              ]}
              rows={preview.data.groups.map((g: PayoutPreviewGroup) => ({
                id: g.pandit_profile_id,
                pandit: g.pandit_public_code ?? `#${g.pandit_profile_id}`,
                dest: g.bank_account_last4 === null ? '—' : `••••${g.bank_account_last4}`,
                count: String(g.payable_count),
                total: <Money paise={g.total_paise} showPaise />,
                // Every reason at once. One at a time sends an operator round a loop.
                why:
                  g.blockers.length === 0
                    ? 'ready'
                    : g.blockers.map((b) => b.detail).join(' '),
              }))}
            />
          )}
        </Section>
      )}

      {batches.isPending && <LoadingState message="Reading the runs…" />}
      {batches.isError && (
        <ErrorState title="Could not read the runs" cause={errorMessage(batches.error)} />
      )}
      {batches.isSuccess && batches.data.items.length === 0 && (
        <EmptyState
          title="No payout runs in this state"
          body="A run appears once somebody previews the payables and builds it."
        />
      )}
      {batches.isSuccess && batches.data.items.length > 0 && (
        <DataTable
          density="compact"
          onRowClick={(row) => setOpenId(Number(row.id))}
          columns={[
            { key: 'on', label: 'Run date' },
            { key: 'count', label: 'Payouts', align: 'right' },
            { key: 'total', label: 'Total', align: 'right', mono: true },
            { key: 'state', label: 'State' },
          ]}
          rows={batches.data.items.map((b) => ({
            id: b.id,
            on: b.batch_on,
            count: String(b.payout_count),
            total: <Money paise={b.total_amount_paise} showPaise />,
            state: <StatusChip tone={batchTone(b.status)} label={b.status.replace(/_/g, ' ')} />,
          }))}
          footer={`${batches.data.total} run${batches.data.total === 1 ? '' : 's'}`}
        />
      )}

      {creating !== null && preview.isSuccess && (
        <ConfirmAction
          open
          title="Build this payout run?"
          confirmLabel="Build"
          phrase="BUILD"
          tone="error"
          consequences={[
            'Every payable in the run is taken out of reach of any other run.',
            'Above the four-eyes ceiling it waits for a second admin; below it, it is approved as it is built.',
            'If anything moved since the preview, this is refused rather than built from newer figures.',
          ]}
          reason="Why — this is written down"
          onCancel={() => setCreating(null)}
          onConfirm={(why) =>
            create.mutate(
              { snapshotId: preview.data.snapshot_id, reason: why, key: creating },
              {
                onSuccess: (batch) => {
                  setCreating(null);
                  toast({
                    tone: 'success',
                    message:
                      batch.status === 'pending_approval'
                        ? 'Built — a second admin has to approve it'
                        : 'Built and approved',
                  });
                },
                onError: (error) => {
                  setCreating(null);
                  fail(error);
                },
              },
            )
          }
        />
      )}
    </>
  );
}

/** Exported for its own test: a half-typed date is not asked for, it is waited on. */
export function monthParam(value: string): string | null {
  return /^\d{4}-\d{2}-\d{2}$/.test(value.trim()) ? value.trim() : null;
}

/** What each temple is owed for an IST month, and the transfers recorded against it. */
function TempleRemittances({ view, onView }: ViewProps) {
  const { scope, toast } = useConsole();
  const [day, setDay] = useState(todayIso());
  const [paying, setPaying] = useState<{ temple: TempleOwedRow; key: string } | null>(null);
  const [utr, setUtr] = useState('');
  const [processedOn, setProcessedOn] = useState(todayIso());
  const period = monthParam(day);
  const month = useTempleRemittanceMonth(period);
  const record = useRecordTempleRemittance();

  return (
    <>
      <FilterBar
        tabs={VIEWS}
        tab={view}
        onTab={onView}
        extra={
          <Input
            label="Month (any day in it)"
            value={day}
            onChange={setDay}
            placeholder="2026-09-01"
          />
        }
      />

      {month.isPending && period !== null && <LoadingState message="Reading the month…" />}
      {month.isError && (
        <ErrorState title="Could not read the month" cause={errorMessage(month.error)} />
      )}
      {month.isSuccess && (
        <>
          {month.data.unattributed_paise > 0 && (
            <Section
              title="Charges no temple can be paid for"
              note="Their booking names no temple — they stay in temple payable until somebody works out whose they are"
            >
              <KV
                rows={[
                  [
                    'Unattributed',
                    <Money key="u" paise={month.data.unattributed_paise} showPaise />,
                  ],
                ]}
              />
            </Section>
          )}
          {month.data.temples.length === 0 ? (
            <EmptyState
              title="No temple charges this month"
              body="A temple appears once a booking carrying its charge is completed in the month."
            />
          ) : (
            <DataTable
              density="compact"
              columns={[
                { key: 'temple', label: 'Temple' },
                { key: 'recognised', label: 'Recognised', align: 'right', mono: true },
                { key: 'remitted', label: 'Remitted', align: 'right', mono: true },
                { key: 'owed', label: 'Owed', align: 'right', mono: true },
                { key: 'act', label: '' },
              ]}
              rows={month.data.temples.map((t) => ({
                id: t.temple_id,
                temple: t.temple_name,
                recognised: <Money paise={t.recognised_paise} showPaise />,
                remitted: <Money paise={t.remitted_paise} showPaise />,
                owed: <Money paise={t.owed_paise} showPaise />,
                act: (
                  <ScopeAction
                    scope={scope}
                    need="finance"
                    size="sm"
                    variant="secondary"
                    disabled={t.owed_paise <= 0}
                    onClick={() => setPaying({ temple: t, key: newIdempotencyKey() })}
                  >
                    {`Record transfer to ${t.temple_name}`}
                  </ScopeAction>
                ),
              }))}
            />
          )}
          {month.data.remittances.length > 0 && (
            <Section title="Transfers recorded" note="Append-only — a mistake is corrected by finance, not edited here">
              <DataTable
                density="compact"
                columns={[
                  { key: 'temple', label: 'Temple' },
                  { key: 'amount', label: 'Amount', align: 'right', mono: true },
                  { key: 'utr', label: 'UTR', mono: true },
                  { key: 'on', label: 'Sent on' },
                ]}
                rows={month.data.remittances.map((r) => ({
                  id: r.id,
                  temple:
                    month.data.temples.find((t) => t.temple_id === r.temple_id)?.temple_name ??
                    `#${r.temple_id}`,
                  amount: <Money paise={r.amount_paise} showPaise />,
                  utr: r.utr,
                  on: r.processed_on,
                }))}
              />
            </Section>
          )}
        </>
      )}

      {paying !== null && month.isSuccess && (
        <ConfirmAction
          open
          title={`Record a transfer to ${paying.temple.temple_name}?`}
          confirmLabel="Record"
          phrase="REMIT"
          tone="error"
          consequences={[
            'This says the money has already left our account.',
            'The amount is what the ledger says the temple is owed for the month — you do not type it.',
            'It cannot be edited afterwards; a mistake needs a compensating entry from finance.',
          ]}
          reason="Why — how this was sent"
          onCancel={() => setPaying(null)}
          onConfirm={(why) =>
            record.mutate(
              {
                templeId: paying.temple.temple_id,
                periodMonth: month.data.period_month,
                utr,
                processedOn,
                reason: why,
                key: paying.key,
              },
              {
                onSuccess: () => {
                  setPaying(null);
                  toast({ tone: 'success', message: `Recorded for ${paying.temple.temple_name}` });
                },
                onError: (error) => {
                  setPaying(null);
                  toast({ tone: 'error', message: errorMessage(error) });
                },
              },
            )
          }
        >
          <KV rows={[['Amount', <Money key="a" paise={paying.temple.owed_paise} showPaise />]]} />
          <Input
            label="UTR"
            value={utr}
            onChange={setUtr}
            placeholder="UTIB0001234567"
            helper="The bank's reference — the only thing reconciliation can match against the statement"
          />
          <Input label="Sent on" value={processedOn} onChange={setProcessedOn} />
        </ConfirmAction>
      )}
    </>
  );
}

/** Three parts of one job: what is owed, what is being sent, and what temples are handed. */
export function Payouts() {
  const [view, setView] = useState<string>('payables');
  if (view === 'payables') return <PayableBoard view={view} onView={setView} />;
  if (view === 'temples') return <TempleRemittances view={view} onView={setView} />;
  return <PayoutRuns view={view} onView={setView} />;
}
